import { z } from 'zod';
import { uuidSchema, nameSchema, notesSchema, paginationSchema } from './common.js';

/**
 * Standard filament diameters in mm
 */
export const FILAMENT_DIAMETERS = [1.75, 2.85] as const;

/**
 * Hex color schema for individual colors
 */
const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format');

/**
 * Color hexes array (for multi-color filaments)
 */
const colorHexesSchema = z.array(hexColorSchema).min(1).max(10);

// ============================================================================
// FILAMENT LIBRARY (Product Definition)
// ============================================================================

/**
 * Create a new filament in the library (custom tenant filament)
 */
export const createFilamentSchema = z.object({
  // Basic Info
  name: nameSchema,
  manufacturerId: uuidSchema,
  typeId: uuidSchema,
  finishId: uuidSchema.nullable().optional(),
  embeddedMaterialId: uuidSchema.nullable().optional(),

  // Color
  colorName: z.string().min(1).max(100).trim(),
  colorHexes: colorHexesSchema,

  // Physical Properties
  diameter: z.number().refine((d) => FILAMENT_DIAMETERS.includes(d as 1.75 | 2.85), {
    message: 'Diameter must be 1.75 or 2.85 mm',
  }),
  standardSpoolWeight: z.number().positive().max(10000).default(1000), // grams
  density: z.number().positive().max(10).nullable().optional(), // g/cm³

  // Print Settings (Advanced, all optional)
  hotendTempMin: z.number().int().min(0).max(500).nullable().optional(),
  hotendTempMax: z.number().int().min(0).max(500).nullable().optional(),
  bedTempMin: z.number().int().min(0).max(200).nullable().optional(),
  bedTempMax: z.number().int().min(0).max(200).nullable().optional(),
  printSpeedMin: z.number().int().min(0).max(1000).nullable().optional(),
  printSpeedMax: z.number().int().min(0).max(1000).nullable().optional(),
  fanSpeedMin: z.number().int().min(0).max(100).nullable().optional(),
  fanSpeedMax: z.number().int().min(0).max(100).nullable().optional(),

  // Drying Settings
  dryingRequired: z.boolean().default(false),
  dryingTemp: z.number().int().min(0).max(100).nullable().optional(),
  dryingTime: z.number().int().min(0).max(48).nullable().optional(), // hours

  notes: notesSchema,
});

export type CreateFilamentInput = z.infer<typeof createFilamentSchema>;

/**
 * Update an existing filament
 */
export const updateFilamentSchema = createFilamentSchema.partial();

export type UpdateFilamentInput = z.infer<typeof updateFilamentSchema>;

/**
 * Query/filter schema for filament library search
 */
export const filamentQuerySchema = paginationSchema.extend({
  search: z.string().max(100).optional(),
  typeId: uuidSchema.optional(),
  manufacturerId: uuidSchema.optional(),
  finishId: uuidSchema.optional(),
  embeddedMaterialId: uuidSchema.optional(),
  diameter: z.coerce.number().optional(),
  sortBy: z.enum(['name', 'createdAt', 'manufacturer', 'type']).default('name'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
  // Filter for system library vs custom
  includeSystem: z.coerce.boolean().default(true),
  includeCustom: z.coerce.boolean().default(true),
});

export type FilamentQuery = z.infer<typeof filamentQuerySchema>;

// ============================================================================
// TENANT FILAMENT (My Library)
// ============================================================================

/**
 * Add a filament to tenant's library
 */
export const addFilamentToLibrarySchema = z.object({
  filamentId: uuidSchema,
  notes: notesSchema,
});

export type AddFilamentToLibraryInput = z.infer<typeof addFilamentToLibrarySchema>;

/**
 * Update tenant-specific notes for a library filament
 */
export const updateTenantFilamentSchema = z.object({
  notes: notesSchema,
});

export type UpdateTenantFilamentInput = z.infer<typeof updateTenantFilamentSchema>;

/**
 * Query/filter schema for tenant's filament library
 */
export const tenantFilamentQuerySchema = paginationSchema.extend({
  search: z.string().max(100).optional(),
  typeId: uuidSchema.optional(),
  manufacturerId: uuidSchema.optional(),
  sortBy: z.enum(['addedAt', 'name', 'manufacturer', 'type']).default('addedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type TenantFilamentQuery = z.infer<typeof tenantFilamentQuerySchema>;

// ============================================================================
// LOOKUP TABLES
// ============================================================================

/**
 * Create a new filament type
 */
export const createFilamentTypeSchema = z.object({
  name: nameSchema,
});

export type CreateFilamentTypeInput = z.infer<typeof createFilamentTypeSchema>;

/**
 * Create a new filament manufacturer
 */
export const createFilamentManufacturerSchema = z.object({
  name: nameSchema,
});

export type CreateFilamentManufacturerInput = z.infer<typeof createFilamentManufacturerSchema>;

/**
 * Create a new filament finish
 */
export const createFilamentFinishSchema = z.object({
  name: nameSchema,
});

export type CreateFilamentFinishInput = z.infer<typeof createFilamentFinishSchema>;

/**
 * Create a new embedded material
 */
export const createEmbeddedMaterialSchema = z.object({
  name: nameSchema,
});

export type CreateEmbeddedMaterialInput = z.infer<typeof createEmbeddedMaterialSchema>;
